/*global sagel10n */

import { getElementsInView } from '@util'
import {
    createListItemTitle,
    createListItemDepartment,
    createListItemCounty,
} from './util'

import html from './template.html'

const template = document.createElement('template')
template.innerHTML = html

customElements.define(
    'municipality-picker-list',
    class extends HTMLElement {
        constructor() {
            super()

            const shadow = this.attachShadow({ mode: 'open' })
            shadow.append(template.content.cloneNode(true))
        }

        connectedCallback() {
            this.observer = new IntersectionObserver((entries) => {
                entries.forEach(async (entry) => {
                    if (entry.isIntersecting) {
                        this.dispatchOffsetChangeEvent();
                    }
                });
            });
        }

        startObserving() {
            this.dispatchOffsetResetEvent();
            this.observer.observe(this.loader);
        }

        stopObserving() {
            this.observer.unobserve(this.loader);
        }

        get wrapper() {
            return this.shadowRoot.querySelector('.wrapper')
        }

        get items() {
            const slot = this.shadowRoot.querySelector('slot')
            const assignedElements = slot.assignedElements()
            return assignedElements
        }

        get loader() {
            return this.shadowRoot.getElementById("infinite-loader");
        }

        /**
         * Dispatches a offsetchange event.
         */
        dispatchOffsetChangeEvent() {
            this.dispatchEvent(
                new CustomEvent('offsetchange', {
                    bubbles: true,
                })
            )
        }
        /**
         * Dispatches a offsetchange event.
         */
        dispatchOffsetResetEvent() {
            this.dispatchEvent(
                new CustomEvent('offsetreset', {
                    bubbles: true,
                })
            )
        }

        get shopURL() {
            const shopURL = new URL('https://www.welzorgshop.nl')
            const shopURLParams = new URLSearchParams()

            shopURLParams.append('utm_source', 'Welzorg+website')
            shopURLParams.append('utm_medium', 'Gemeentekiezer')
            shopURLParams.append('utm_campaign', 'landkaart')

            shopURL.search = shopURLParams
            return shopURL
        }

        /**
         * @typedef {object} MunicipalityObject
         * @property {string} title
         * @property {string} statcode
         * @property {string} href
         * @property {array} departments
         */

        /**
         * Clears the list and renders municipality-picker-item elements as it's children.
         * @param {MunicipalityObject[]} items
         * @param clearList
         */
        async renderItems(items, clearList = false) {
            if (clearList === true) {
                await this.clearList();
            }

            if (items.length === 0) {
                const itemElement = document.createElement(
                    'municipality-picker-item'
                )
                itemElement.title = 'Geen resultaten'
                itemElement.type = 'error'
                itemElement.active = false
                itemElement.className = 'municipality-item-ani--in'

                const titleElement = createListItemTitle(sagel10n.noResults)
                itemElement.append(titleElement)

                const countyElement = createListItemCounty(
                    sagel10n.noMunicipalitiesSearch
                )
                itemElement.append(countyElement)

                this.append(itemElement)
                return
            }

            items.forEach(
                (
                    { title, statcode, href, county, departments, active },
                    index
                ) => {
                    const itemElement = document.createElement(
                        'municipality-picker-item'
                    )
                    itemElement.id = statcode
                    itemElement.href = href
                    itemElement.title = title
                    itemElement.departments = departments
                    itemElement.active = active
                    itemElement.tooltip = sagel10n.activeMunicipality
                    itemElement.className = 'municipality-item-ani--in'
                    itemElement.style.animationDelay = `${50 * index}ms`

                    const titleElement = createListItemTitle(title)
                    itemElement.append(titleElement)

                    const countyElement = createListItemCounty(county)
                    itemElement.append(countyElement)

                    for (const { link, title, type } of departments) {
                        const icon = createListItemDepartment(title, link, type)
                        itemElement.append(icon)
                    }

                    const shop = createListItemDepartment(
                        sagel10n.shopTitle,
                        this.shopURL,
                        'shop'
                    )
                    itemElement.append(shop)

                    this.append(itemElement)
                }
            )
        }

        /**
         * Selects and highlights a single municipality from the list.
         * @param {string} statcode Municipality code
         * @returns {void}
         */
        selectItem(statcode) {
            const child = [...this.children].find(({ id }) => id === statcode)
            if (child === undefined) {
                return
            }

            child.highlight = true
            child.scrollIntoView({
                block: 'nearest',
                inline: 'nearest',
                behavior: 'smooth',
            })
        }

        /**
         * Deselects all items from the list.
         * @returns {void}
         */
        deselectItems() {
            for (const child of this.children) {
                child.highlight = false
            }
        }

        /**
         * Removes all items from the list.
         * First it animates out all elements that are in view.
         * After all animations are complete it will remove all child elements.
         * @returns {void}
         */
        async clearList() {
            if (this.infiniteLoaderEl) {
                this.dispatchOffsetChangeEvent(0);
            }

            const childrenInView = await getElementsInView(
                'municipality-picker-item',
                true,
                {
                    root: this,
                }
            )

            const animations = childrenInView.map(
                (child, index) =>
                    new Promise(resolve => {
                        child.classList.replace(
                            'municipality-item-ani--in',
                            'municipality-item-ani--out'
                        )
                        child.style.animationDelay = `${50 * index}ms`
                        child.onanimationend = () => resolve()
                    })
            )

            await Promise.all(animations)

            while (this.firstElementChild) {
                this.firstElementChild.remove()
            }
        }
    }
)



// WEBPACK FOOTER //
// ./scripts/web-components/municipality-picker-list/index.js